import { FilterPanel } from "@components/FilterPanel";
import { Opportunities } from "@components/Opportunities";
import { RepSelector } from "@components/RepSelector";
import { RepTop30 } from "@components/RepTop30";
import { ResultsPanel } from "@components/ResultsPanel";
import { resetRepTop30, setSelectedRep } from "@app-redux/slices/repTop30Slice";
import {
  clearOpportunitySelection,
  setSelectedCategory,
} from "@app-redux/slices/opportunitySlice";
import { updateFilterCategoriesArr } from "@app-redux/slices/filtersSlice";
import {
  categorySelector,
  filtersSelector,
  repTop30Selector,
  opportunitySelector,
} from "@app-redux/selectors";
import { useAppDispatch, useAppSelector } from "@app-redux/store";
import { getOpportunityCategories } from "@app-redux/thunk/opportunityThunk";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import React, { useState, useCallback, useEffect, useMemo } from "react";
import styles from "./styles.module.css";

type ViewMode = "profiles" | "top30" | "opportunities";

const CATEGORY_DISPLAY_ORDER = [
  "Concert Tours North America",
  "Concert Tours Europe",
  "Corporate Events North America",
  "Corporate Events Europe",
  "Theater North America",
  "Theater Europe",
];

export const Main = () => {
  const dispatch = useAppDispatch();
  const [viewMode, setViewMode] = useState<ViewMode>("profiles");
  const [showTouringChips, setShowTouringChips] = useState(false);
  const { data: categories } = useAppSelector(categorySelector);
  const { categories_values: selectedFilters = [] } =
    useAppSelector(filtersSelector);
  const { selectedRep } = useAppSelector(repTop30Selector);
  const { categories: oppCategories, selectedCategoryId } =
    useAppSelector(opportunitySelector);

  const sortedCategories = useMemo(
    () =>
      [...oppCategories].sort((a, b) => {
        const aIndex = CATEGORY_DISPLAY_ORDER.indexOf(a.name);
        const bIndex = CATEGORY_DISPLAY_ORDER.indexOf(b.name);
        const aPos = aIndex === -1 ? CATEGORY_DISPLAY_ORDER.length : aIndex;
        const bPos = bIndex === -1 ? CATEGORY_DISPLAY_ORDER.length : bIndex;
        return aPos - bPos;
      }),
    [oppCategories]
  );

  useEffect(() => {
    dispatch(getOpportunityCategories());
  }, [dispatch]);

  const handleViewChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
      if (!newMode || newMode === viewMode) return;

      if (viewMode === "opportunities") {
        dispatch(clearOpportunitySelection());
        setShowTouringChips(false);
      }

      if (viewMode === "top30") {
        if (selectedRep) {
          const isAlreadyFiltered = selectedFilters.includes(selectedRep.id);
          if (!isAlreadyFiltered) {
            dispatch(
              updateFilterCategoriesArr({
                id: selectedRep.id,
                checked: false,
              })
            );
          }
        }
        dispatch(resetRepTop30());
      }

      if (newMode === "top30") {
        const repCategory = categories.find((cat) => cat.name === "Rep");
        if (repCategory) {
          const repIds = new Set(repCategory.items.map((r) => r.id));
          const selectedRepId = selectedFilters.find((id) => repIds.has(id));
          if (selectedRepId) {
            const rep = repCategory.items.find((r) => r.id === selectedRepId);
            if (rep) {
              dispatch(setSelectedRep({ id: rep.id, name: rep.name }));
            }
          }
        }
      }

      setViewMode(newMode);
    },
    [viewMode, categories, selectedFilters, selectedRep, dispatch]
  );

  const handleTouringClick = useCallback(() => {
    if (viewMode === "opportunities") {
      dispatch(clearOpportunitySelection());
      setShowTouringChips(false);
      setViewMode("profiles");
    } else {
      if (viewMode === "top30") {
        if (selectedRep) {
          const isAlreadyFiltered = selectedFilters.includes(selectedRep.id);
          if (!isAlreadyFiltered) {
            dispatch(
              updateFilterCategoriesArr({
                id: selectedRep.id,
                checked: false,
              })
            );
          }
        }
        dispatch(resetRepTop30());
      }
      setShowTouringChips(true);
      if (sortedCategories.length > 0) {
        dispatch(setSelectedCategory(sortedCategories[0].id));
        setViewMode("opportunities");
      }
    }
  }, [viewMode, selectedRep, selectedFilters, sortedCategories, dispatch]);

  const handleCategoryClick = useCallback(
    (categoryId: string) => {
      if (viewMode === "opportunities" && selectedCategoryId === categoryId) {
        return;
      }

      dispatch(setSelectedCategory(categoryId));
      setViewMode("opportunities");
    },
    [viewMode, selectedCategoryId, dispatch]
  );

  return (
    <div className={styles.main}>
      <div className={styles.topBar}>
        <ToggleButtonGroup
          value={viewMode !== "opportunities" ? viewMode : null}
          exclusive
          onChange={handleViewChange}
          size="small"
        >
          <ToggleButton value="profiles">All Profiles</ToggleButton>
          <ToggleButton value="top30">Rep Top 30</ToggleButton>
        </ToggleButtonGroup>
        <Divider orientation="vertical" flexItem />
        <ToggleButton
          value="touring"
          selected={showTouringChips || viewMode === "opportunities"}
          onChange={handleTouringClick}
          size="small"
        >
          Touring and Show Listings
        </ToggleButton>
        {(showTouringChips || viewMode === "opportunities") && (
          <>
            <Divider orientation="vertical" flexItem />
            <div className={styles.categoryChips}>
              {sortedCategories.map((cat) => {
                const isSelected = selectedCategoryId === cat.id;
                return (
                  <Chip
                    key={cat.id}
                    label={cat.name}
                    variant={isSelected ? "filled" : "outlined"}
                    color={isSelected ? "primary" : "default"}
                    onClick={() => handleCategoryClick(cat.id)}
                    className={styles.categoryChip}
                    sx={isSelected ? {
                      backgroundColor: "primary.main",
                      color: "#fff",
                      fontWeight: 600,
                      "&:hover": { backgroundColor: "primary.dark" },
                    } : undefined}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
      <div className={styles.contentArea}>
        {viewMode === "opportunities" ? (
          <Opportunities />
        ) : (
          <>
            <div className={styles.leftPanel}>
              {viewMode === "profiles" ? <FilterPanel /> : <RepSelector />}
            </div>
            {viewMode === "profiles" ? <ResultsPanel /> : <RepTop30 />}
          </>
        )}
      </div>
    </div>
  );
};
